import { useRef } from 'react';
import { pxToRem, useStyles } from '@archipro-design/aria';
import { BannerAdTile } from '@archipro-design/aria';
import type { AdvertCreative, AdvertTileSize } from '@modules/adverts/type';
import * as S from './AdvertBannerTile.style';
import { useAdEventHandler, useTracker } from '@archipro-website/tracker';
import { ArrowNextLine } from '@archipro-design/icons';
import { createLinkedComponentParams } from '~/utils/urlHelper';
import { getImagesSizes } from '~/utils/image-sizes-utils';

interface AdvertTileProp {
    advertCreative: AdvertCreative;
    desktop: boolean;
    columns: 1 | 2 | 3 | 4 | 5;
}

const AdvertBannerTile: React.FC<AdvertTileProp> = ({
    advertCreative,
    desktop,
    columns,
}) => {
    const bannerRef = useRef(null);
    const { css } = useStyles({ columns });
    const { onClick } = useAdEventHandler(advertCreative, bannerRef);
    const tracker = useTracker();

    let size: AdvertTileSize = '03';
    if (desktop) {
        size = '01';
    }

    const bannerTile = (
        <BannerAdTile
            ref={bannerRef}
            variant={size}
            images={advertCreative.carousel.map((image) => {
                let imageUrl = '';
                if (desktop) {
                    imageUrl = image.image.desktop;
                } else {
                    imageUrl = image.image.mobile;
                }

                return {
                    alt:
                        advertCreative.title ||
                        advertCreative.textContent ||
                        '',
                    src: imageUrl,
                    width: '100%',
                    height: '100%',
                    overlay: {
                        ...createLinkedComponentParams(
                            tracker,
                            'Ad_ImageClick',
                            advertCreative.cta.href,
                            {
                                libraryItemID: advertCreative.professional.id,
                                professionalID: advertCreative.professional.id,
                                professionalTitle:
                                    advertCreative.professional.title,
                            },
                            null,
                            onClick
                        ),
                        prefetch: 'intent',
                        icon: <></>,
                    },
                    sizes: getImagesSizes({
                        desktop: 1300,
                        mobile: 382,
                    }),
                };
            })}
            company={advertCreative.professional.title}
            title={advertCreative.title}
            description={advertCreative.textContent}
            actionButton={{
                color: 'white',
                className: css(S.CTAButton),
                ...createLinkedComponentParams(
                    tracker,
                    'Ad_CTAClick',
                    advertCreative.cta.href,
                    {
                        libraryItemID: advertCreative.professional.id,
                        professionalID: advertCreative.professional.id,
                        professionalTitle: advertCreative.professional.title,
                    },
                    null,
                    onClick
                ),
                prefetch: 'intent',
                children: advertCreative.cta.label,
                iconAfter: <ArrowNextLine />,
                size: !desktop ? 14 : 24,
                variables: {
                    iconPadding: 0,
                    buttonSize14IconXMargin: !desktop ? pxToRem(8) : pxToRem(4),
                },
            }}
            size={'medium'}
        />
    );

    return <div className={css(S.Container)}>{bannerTile}</div>;
};

export default AdvertBannerTile;
